import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for certain endpoints that use 401 for validation errors
      const skipRedirectUrls = ['/settings/resend-keys'];
      const requestUrl = error.config?.url || '';
      const shouldSkipRedirect = skipRedirectUrls.some(url => requestUrl.includes(url));
      
      if (!shouldSkipRedirect) {
        localStorage.removeItem('token');
        if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Cars API
export const carsApi = {
  getAll: (params) => api.get('/cars', { params }),
  getFeatured: (limit = 6) => api.get(`/cars/featured?limit=${limit}`),
  getById: (id) => api.get(`/cars/${id}`),
  create: (data) => api.post('/cars', data),
  update: (id, data) => api.put(`/cars/${id}`, data),
  delete: (id) => api.delete(`/cars/${id}`),
  getFilterOptionsWithCounts: (params = {}) => api.get('/cars/filter-options', { params })
};

// Inquiries API
export const inquiriesApi = {
  getAll: (params) => api.get('/inquiries', { params }),
  getActiveInquiries: () => api.get('/inquiries/list/active'),
  getArchivedInquiries: () => api.get('/inquiries/list/archived'),
  clearArchivedInquiries: (olderThanDays = 30) => api.delete(`/inquiries/archived/clear?older_than_days=${olderThanDays}`),
  archiveInquiry: (id) => api.post(`/inquiries/${id}/archive`),
  getById: (id) => api.get(`/inquiries/${id}`),
  create: (data) => api.post('/inquiries', data),
  reply: (id, data) => api.post(`/inquiries/${id}/reply`, data),
  delete: (id) => api.delete(`/inquiries/${id}`)
};

// Media API
export const mediaApi = {
  getAll: () => api.get('/media'),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // NOTE: the shared `api` instance has a default 'Content-Type: application/json'.
    // axios 1.x converts FormData to JSON when a JSON content-type is present, which
    // makes the backend reject the request with 422. Use a bare axios call so the
    // browser sets multipart/form-data with the correct boundary.
    const token = localStorage.getItem('token');
    return axios.post(`${API_URL}/api/media/upload`, formData, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  },
  delete: (id) => api.delete(`/media/${id}`)
};

// Contact API
export const contactApi = {
  submit: (data) => api.post('/contact', data)
};

// Filter Options API
export const filtersApi = {
  getOptions: () => api.get('/filters/options')
};

// Analytics API
export const analyticsApi = {
  getOverview: () => api.get('/analytics/overview'),
  getFilterStats: () => api.get('/analytics/filter-stats'),
  trackPageView: (page, carId = null, sessionId = null) => 
    api.post('/analytics/track', null, { params: { page, car_id: carId, session_id: sessionId } }),
  trackFilter: (filterType, filterValue, sessionId = null) =>
    api.post('/analytics/track-filter', null, { params: { filter_type: filterType, filter_value: filterValue, session_id: sessionId } })
};

// Stats API
export const statsApi = {
  get: () => api.get('/stats')
};

// Seed API
export const seedApi = {
  seed: () => api.post('/seed')
};

// Favorites API
export const favoritesApi = {
  add: (carId, sessionId) => api.post('/favorites', { car_id: carId, session_id: sessionId }),
  getAll: (sessionId) => api.get(`/favorites/${sessionId}`),
  remove: (sessionId, carId) => api.delete(`/favorites/${sessionId}/${carId}`),
  count: (sessionId) => api.get(`/favorites/${sessionId}/count`),
  check: (sessionId, carId) => api.get(`/favorites/${sessionId}/check/${carId}`)
};

// Settings API
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  updateAdminStatus: (online) => api.post(`/settings/admin-status?online=${online}`)
};

// Comparison API
export const compareApi = {
  get: (sessionId) => api.get(`/compare/${sessionId}`),
  set: (carIds, sessionId) => api.post('/compare', { car_ids: carIds, session_id: sessionId }),
  remove: (sessionId, carId) => api.delete(`/compare/${sessionId}/${carId}`),
  clear: (sessionId) => api.delete(`/compare/${sessionId}`)
};

// Newsletter API
export const newsletterApi = {
  subscribe: (email, name = null, source = 'website') => api.post('/newsletter/subscribe', { email, name, source }),
  unsubscribe: (subscriberId) => api.get(`/newsletter/unsubscribe/${subscriberId}`),
  getSubscribers: (activeOnly = true) => api.get(`/newsletter/subscribers?active_only=${activeOnly}`),
  addSubscriber: (email, name) => api.post('/newsletter/subscribers', { email, name, source: 'admin' }),
  removeSubscriber: (subscriberId) => api.delete(`/newsletter/subscribers/${subscriberId}`),
  getStats: () => api.get('/newsletter/stats'),
  send: (subject, content, includeNewCars = true, carIds = []) => 
    api.post('/newsletter/send', { subject, content, include_new_cars: includeNewCars, car_ids: carIds }),
  getLogs: (limit = 20) => api.get(`/newsletter/logs?limit=${limit}`)
};

// Price Alerts API
export const priceAlertApi = {
  create: (carId, email, targetPrice, name = null) => 
    api.post('/price-alerts', { car_id: carId, email, target_price: targetPrice, name }),
  get: (alertId) => api.get(`/price-alerts/${alertId}`),
  delete: (alertId) => api.delete(`/price-alerts/${alertId}`),
  getAll: (activeOnly = true) => api.get(`/price-alerts?active_only=${activeOnly}`),
  getStats: () => api.get('/price-alerts/stats')
};

// Test Drive API
export const testDriveApi = {
  // Calendar
  getCalendar: () => api.get('/test-drive/calendar'),
  getAutoCalendar: (year, month) => api.get(`/test-drive/auto-calendar/${year}/${month}`),
  getDayAvailability: (date) => api.get(`/test-drive/calendar/${date}`),
  updateDay: (date, data) => api.put(`/test-drive/calendar/${date}`, data),
  updateCalendarBulk: (days) => api.post('/test-drive/calendar/bulk', days),
  
  // Bookings
  book: (data) => api.post('/test-drive/book', data),
  adminBook: (data) => api.post('/test-drive/admin/book', data),
  getBookings: (status = null) => api.get('/test-drive/bookings', { params: { status } }),
  getActiveBookings: () => api.get('/test-drive/bookings/active'),
  getArchivedBookings: () => api.get('/test-drive/bookings/archived'),
  clearArchivedBookings: (olderThanDays = 30) => api.delete(`/test-drive/bookings/archived/clear?older_than_days=${olderThanDays}`),
  updateBookingStatus: (bookingId, action, rejectReason = null) => 
    api.put(`/test-drive/bookings/${bookingId}`, { action, reject_reason: rejectReason }),
  deleteBooking: (bookingId) => api.delete(`/test-drive/bookings/${bookingId}`),
  
  // Available slots
  getAvailableSlots: (date, carId = null) => 
    api.get(`/test-drive/available-slots/${date}`, { params: { car_id: carId } })
};

// Resend Keys API
export const resendApi = {
  getKeys: () => api.get('/settings/resend-keys'),
  updateKeys: (data) => api.put('/settings/resend-keys', data)
};

export default api;
